import * as React from 'react';
import {
    BaseComponent,
    classNamesFunction,
    css,
    styled,
} from 'office-ui-fabric-react/lib/Utilities';
import { FocusZone, FocusZoneDirection } from 'office-ui-fabric-react/lib/FocusZone';
import { getStyles } from 'office-ui-fabric-react/lib/components/DetailsList/DetailsRow.styles';
import {
    IDetailsRowProps,
    IDetailsRowStyleProps,
    IDetailsRowStyles,
} from 'office-ui-fabric-react/lib/DetailsList';
import {
    SELECTION_CHANGE,
    SelectionMode,
} from 'office-ui-fabric-react/lib/utilities/selection/interfaces';

const getClassNames = classNamesFunction<IDetailsRowStyleProps, IDetailsRowStyles>();

interface CustomDetailsRowBaseProps extends IDetailsRowProps {
    focusZoneRef?: (focusZone: FocusZone) => void;
}

interface CustomDetailsRowBaseState {
    isSelected: boolean;
    isSelectionModal: boolean;
}

/**
 * This component allows the user to have a custom row in details list
 * but still being able to maintain the style and accessiblity properties (focus etc.)
 * of the original details row with custom content.
 * The code here is similar to the DetailsRow implementation in Fabric
 */
class CustomDetailsRowBase extends BaseComponent<
    CustomDetailsRowBaseProps,
    CustomDetailsRowBaseState
> {
    constructor(props: CustomDetailsRowBaseProps) {
        super(props);

        this.state = {
            ...this.getSelectionState(props),
        };
    }

    componentDidMount() {
        // We need to change the selection
        // information when the selection fo the rows change.
        // Hence we subscribe to selection change event.
        this._events.on(this.props.selection, SELECTION_CHANGE, this.onSelectionChanged);
    }

    componentWillReceiveProps(newProps: IDetailsRowProps) {
        this.setState({ ...this.getSelectionState(newProps) });
    }

    render() {
        const {
            itemIndex,
            styles,
            theme,
            compact,
            checkboxCellClassName,
            className,
            selectionMode,
            focusZoneRef,
        } = this.props;
        const { isSelected, isSelectionModal } = this.state;
        const isContentUnselectable = selectionMode === SelectionMode.multiple;
        const styleClassNames = getClassNames(styles, {
            isSelected,
            theme,
            compact,
            checkboxCellClassName,
            className: className,
            canSelect: !isContentUnselectable,
            anySelected: isSelectionModal,
        });

        return (
            <FocusZone
                ref={focusZoneRef}
                className={css(styleClassNames.root)}
                direction={FocusZoneDirection.horizontal}
                role="row"
                data-is-focusable={true}
                data-selection-index={itemIndex}
                data-item-index={itemIndex}
                aria-rowindex={itemIndex + 1}
                data-automationid="DetailsRow"
                allowFocusRoot={true}>
                {this.props.children}
            </FocusZone>
        );
    }

    private getSelectionState(
        props: IDetailsRowProps
    ): Pick<CustomDetailsRowBaseState, 'isSelected' | 'isSelectionModal'> {
        const { itemIndex, selection } = props;
        return {
            isSelected: !!selection && selection.isIndexSelected(itemIndex),
            isSelectionModal: !!selection && !!selection.isModal && selection.isModal(),
        };
    }

    private onSelectionChanged = () => {
        const selectionState = this.getSelectionState(this.props);

        if (
            this.state.isSelected !== selectionState.isSelected ||
            this.state.isSelectionModal !== selectionState.isSelectionModal
        ) {
            // When there is a state change then update to new state
            this.setState({
                ...selectionState,
            });
        }
    };
}

// This will link the details row styles to this custom row component
export const CustomDetailsRow = styled<
    CustomDetailsRowBaseProps,
    IDetailsRowStyleProps,
    IDetailsRowStyles
>(CustomDetailsRowBase, getStyles, undefined, { scope: 'DetailsRow' });
